import { useState, useCallback } from 'react';
import axios from 'axios';
import * as constants from '../../../utils/constants';

const API_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;

/**
 * Hook for interacting with Outlook email API
 * Connects to GatewayX Backend /outlook/ endpoints
 */
export const useOutlookEmail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    return {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  // Get a single email message
  const getMessage = useCallback(async (messageId, connectionId = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = connectionId ? { connection_id: connectionId } : {};
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/messages/${messageId}/`,
        { headers: getAuthHeaders(), params }
      );
      return transformMessage(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch message');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Get messages list
  const getMessages = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { folderId, top = 50, skip = 0, search, connectionId } = options;
      const params = {
        top,
        skip,
        ...(folderId && { folder_id: folderId }),
        ...(search && { search }),
        ...(connectionId && { connection_id: connectionId }),
      };
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/messages/`,
        { headers: getAuthHeaders(), params }
      );
      return {
        messages: (response.data.messages || []).map(transformMessage),
        nextPageToken: response.data.next_page_token,
        total: response.data.total,
      };
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch messages');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Get mail folders
  const getFolders = useCallback(async (connectionId = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = connectionId ? { connection_id: connectionId } : {};
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/folders/`,
        { headers: getAuthHeaders(), params }
      );
      return response.data.folders || [];
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch folders');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Convert File to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:mime;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve({
          name: file.name,
          content_type: file.type || 'application/octet-stream',
          content: base64,
        });
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Send email
  const sendEmail = useCallback(async (emailData) => {
    setLoading(true);
    setError(null);
    try {
      // Convert File objects to base64
      let attachments = [];
      if (emailData.attachments && emailData.attachments.length > 0) {
        attachments = await Promise.all(
          emailData.attachments.map(file => fileToBase64(file))
        );
      }

      const payload = {
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.message,
        body_type: emailData.bodyType || 'HTML',
        ...(emailData.cc && { cc: emailData.cc }),
        ...(emailData.bcc && { bcc: emailData.bcc }),
        ...(emailData.importance && { importance: emailData.importance }),
        ...(attachments.length > 0 && { attachments }),
        ...(emailData.connectionId && { connection_id: emailData.connectionId }),
      };
      const response = await axios.post(
        `${API_URL}/api/v1/outlook/messages/send/`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send email');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Reply to email
  const replyEmail = useCallback(async (messageId, comment, replyAll = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/outlook/messages/${messageId}/reply/`,
        { comment, reply_all: replyAll },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reply to email');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Forward email
  const forwardEmail = useCallback(async (messageId, toRecipients, comment = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/outlook/messages/${messageId}/forward/`,
        { to: toRecipients, comment },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to forward email');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Mark as read/unread
  const markAsRead = useCallback(async (messageId, isRead = true) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/v1/outlook/messages/${messageId}/`,
        { is_read: isRead },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update message');
      throw err;
    }
  }, [getAuthHeaders]);

  // Delete email
  const deleteMessage = useCallback(async (messageId) => {
    try {
      await axios.delete(
        `${API_URL}/api/v1/outlook/messages/${messageId}/`,
        { headers: getAuthHeaders() }
      );
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete message');
      throw err;
    }
  }, [getAuthHeaders]);

  // Move email to folder
  const moveMessage = useCallback(async (messageId, destinationFolderId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/outlook/messages/${messageId}/move/`,
        { destination_id: destinationFolderId },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to move message');
      throw err;
    }
  }, [getAuthHeaders]);

  // Get attachments
  const getAttachments = useCallback(async (messageId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/messages/${messageId}/attachments/`,
        { headers: getAuthHeaders() }
      );
      return response.data.attachments || [];
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch attachments');
      throw err;
    }
  }, [getAuthHeaders]);

  return {
    loading,
    error,
    getMessage,
    getMessages,
    getFolders,
    sendEmail,
    replyEmail,
    forwardEmail,
    markAsRead,
    deleteMessage,
    moveMessage,
    getAttachments,
  };
};

/**
 * Transform backend message format to Mail-0/Zero format
 */
function transformMessage(msg) {
  if (!msg) return null;
  
  return {
    id: msg.id || msg.outlook_message_id,
    threadId: msg.conversation_id || msg.thread_id,
    subject: msg.subject || '',
    sender: {
      email: msg.sender_email || msg.from?.emailAddress?.address || '',
      name: msg.sender_name || msg.from?.emailAddress?.name || '',
    },
    to: (msg.recipients || msg.toRecipients || []).map(r => ({
      email: r.email || r.emailAddress?.address || '',
      name: r.name || r.emailAddress?.name || '',
    })),
    cc: (msg.cc_recipients || msg.ccRecipients || []).map(r => ({
      email: r.email || r.emailAddress?.address || '',
      name: r.name || r.emailAddress?.name || '',
    })),
    bcc: (msg.bcc_recipients || msg.bccRecipients || []).map(r => ({
      email: r.email || r.emailAddress?.address || '',
      name: r.name || r.emailAddress?.name || '',
    })),
    receivedOn: msg.received_time || msg.receivedDateTime,
    unread: !msg.is_read && msg.isRead !== true,
    body: msg.body_content || msg.body?.content || '',
    bodyPreview: msg.body_preview || msg.bodyPreview || '',
    decodedBody: msg.body_content || msg.body?.content || '',
    importance: msg.importance || 'normal',
    hasAttachments: msg.has_attachments || msg.hasAttachments || false,
    webLink: msg.web_link || msg.webLink || '',
    tags: [],
    attachments: (msg.attachments || []).map(att => ({
      attachmentId: att.id,
      filename: att.name || att.filename,
      mimeType: att.contentType || att.mime_type,
      size: att.size || 0,
    })),
  };
}

export default useOutlookEmail;
